import { useState, FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLogin } from "../queries/authQueries";
import { useAuthStore } from "../stores/authStore";
import { ApiError } from "../lib/apiClient";

import {
  Container,
  Paper,
  Title,
  TextInput,
  PasswordInput,
  Button,
  Text,
  Anchor,
  Stack,
  Alert,
} from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";

export default function LoginPage() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const { mutate: login, isPending, error } = useLogin();
  const setCredentials = useAuthStore((state) => state.setCredentials);
  const navigate = useNavigate();

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    login(
      { email, password },
      {
        onSuccess: (response) => {
          setCredentials(response.user, response.access_token);
          navigate("/");
        },
        onError: (err) => {
          console.error("Login failed:", err);
        },
      }
    );
  };

  const errorMessage =
    error instanceof ApiError &&
    typeof error.data === "object" &&
    error.data &&
    "detail" in error.data
      ? String(error.data.detail)
      : t("auth.login.error");

  return (
    <Container size="xs" py="xl">
      <Paper withBorder shadow="md" p={30} radius="md">
        <Title order={2} ta="center" mb="md">
          {t("auth.login.title")}
        </Title>

        <form onSubmit={handleSubmit}>
          <Stack gap="md">
            <TextInput
              label={t("auth.login.email")}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />

            <PasswordInput
              label={t("auth.login.password")}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />

            {error && (
              <Alert icon={<IconAlertCircle size={16} />} color="red">
                {errorMessage}
              </Alert>
            )}

            <Button type="submit" fullWidth color="brand" loading={isPending}>
              {t("auth.login.submit")}
            </Button>
          </Stack>
        </form>

        <Text ta="center" mt="md" size="sm">
          {t("auth.login.noAccount")}{" "}
          <Anchor component={Link} to="/register" fw={500}>
            {t("auth.login.registerLink")}
          </Anchor>
        </Text>
      </Paper>
    </Container>
  );
}
